/**
 * The API client.
 *
 * ── Authentication is a cookie, not a token in JavaScript ──
 *
 * The session is an httpOnly cookie, so this file cannot read it and neither can an XSS
 * (scope §8, moving off the legacy `localStorage` JWT). There is consequently no token
 * handling here at all — the browser attaches it, and a 401 or a redirect to the login
 * page is how we learn it has gone.
 *
 * A rejected request sends the user to the server-rendered sign-in page rather than
 * showing a login form here. That page already exists and is tested, including the
 * lockout, the timing-safe comparison and the open-redirect guard — reimplementing it in
 * the SPA would be a second implementation of the most security-sensitive screen.
 */

export class ApiError extends Error {
  constructor(readonly status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

function toLogin(): never {
  const next = encodeURIComponent(window.location.pathname + window.location.search);
  window.location.href = `/auth/login?next=${next}`;
  // Unreachable, but the signature says this never returns normally.
  throw new ApiError(401, 'redirecting to sign in');
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: { accept: 'application/json', ...(init.headers ?? {}) },
    // Same-origin in dev (via the Vite proxy) and in production.
    credentials: 'same-origin',
    redirect: 'manual',
  });

  /*
   * The guard redirects rather than returning 401, because it also serves browsers
   * asking for HTML. `redirect: 'manual'` surfaces that as an opaque response instead of
   * silently following it and handing us a login page parsed as JSON.
   */
  if (response.type === 'opaqueredirect' || response.status === 401) toLogin();

  if (!response.ok) {
    let message = `${response.status}`;
    try {
      const body = await response.json();
      message = (body as { message?: string; error?: string }).message
        ?? (body as { error?: string }).error ?? message;
    } catch { /* not JSON; the status is all we have */ }
    throw new ApiError(response.status, message);
  }

  return response.json() as Promise<T>;
}

// ---- Shapes, mirroring @pdmg/data-access -----------------------------------

export interface Page<T> {
  rows: T[]; total: number; page: number; pageSize: number;
  sort: string; direction: 'asc' | 'desc';
}
export interface Agency {
  id: string; companyName: string; email: string; phone: string;
  city: string; state: string; isActive: boolean;
}
export interface Client extends Agency { agencyId: string }
export interface Campaign {
  id: string; agencyId: string; clientId: string;
  name: string; io: string; so: string; reportSo: string;
  /** Resolved for the list; empty when the client row is gone. */
  clientName: string;
  status: string; isActive: boolean;
  createDatetime: string | null;
  startDatetime: string | null; endDatetime: string | null;
  overallBudget: number | null; dailyBudget: number | null; whiteLabel: string | null;
  counts?: { adGroups: number; ads: number };
}
export interface AdGroup {
  id: string; campaignId: string; name: string;
  startDateTime: string | null; endDateTime: string | null;
  overallBudget: number | null; dailyBudget: number | null; bid: number | null;
  geoType: string | null; radius: number | null; isActive: boolean;
}
export interface AdCreative {
  kind: 'image' | 'audio' | 'video' | 'text' | 'none';
  imageUrl: string | null;
  /** The playable video asset (MP4) for `video` kind; `imageUrl` carries its still snapshot. */
  videoUrl: string | null;
  audioUrl: string | null;
  width: number | null;
  height: number | null;
}
export interface Ad {
  id: string; campaignId: string; adGroupId: string;
  type: string; title: string; description: string;
  destinationUrl: string; displayUrl: string; originalUrl: string; isActive: boolean;
  creative: AdCreative | null;
}

export interface CampaignPacing {
  start: string | null; end: string | null;
  campaignDuration: number | null; daysIntoCampaign: number | null;
  /** Negative once the campaign has finished. */
  daysRemaining: number | null;
  targetClicks: number | null; targetImpressions: number | null;
  clicks: number; impressions: number;
  clicksShouldBeAt: number | null; impressionsShouldBeAt: number | null;
}

export interface CampaignDelivery {
  clicks: number; impressions: number; spend: number;
  targetImpressions: number | null; targetClicks: number | null;
  overallBudget: number | null; dailyBudget: number | null;
  latestDate: string | null;
  /** Why a campaign is not delivering, in the legacy monitor's words. */
  flags: string[];
  isActive: boolean | null;
  /** Measured from recent delivery, not from a stored flag — see deliveringFrom(). */
  isDelivering: boolean | null;
  lastDeliveryDate: string | null;
  /** Where clicks should stand today, as Sails computed it. */
  clicksShouldBeAt: number | null;
  daysRemaining: number | null;
  daysIntoCampaign: number | null;
  campaignDuration: number | null;
  /** Decided on the server, with the same band that drives the "behind" filter. */
  pace: 'ahead' | 'behind' | 'even' | 'unknown';
}

/** Per-row delivery for the ad group table. `todaySpend` is the newest day alone. */
export interface AdGroupRowDelivery extends CampaignDelivery {
  todaySpend: number;
}

export interface AdGroupDelivery {
  summary: {
    clicks: number; impressions: number; retargetImpressions: number;
    spend: number; targetImpressions: number | null;
    firstDate: string | null; lastDate: string | null; days: number;
  };
  byAd: Record<string, { clicks: number; impressions: number; spend: number }>;
  /** Impressions + clicks per day, for the delivery chart. */
  byDay: DeliveryDay[];
  /** True when the AD rows do not sum to the ADGROUP total — see adGroupDelivery(). */
  adTotalsDiffer: boolean;
}

export interface ListParams {
  page?: number; pageSize?: number; sort?: string;
  direction?: 'asc' | 'desc'; search?: string;
  /** Campaigns and ads. Absent means every status — see statusFilter() on the server. */
  status?: 'active' | 'inactive';
  /** Campaigns only: just those behind their click pacing today. */
  behind?: boolean;
  /** Campaigns only: only those whose flight overlaps this window (YYYY-MM-DD) — "ran during". */
  ranFrom?: string;
  ranTo?: string;
  /** Campaigns only: only those CREATED in the system within this window (YYYY-MM-DD) — by `date_created`. */
  createdFrom?: string;
  createdTo?: string;
  /** Campaigns only: restrict to these clients (multi-select). Sent comma-joined; empty = no filter. */
  clientIds?: string[];
}

/** A client option for the spreadsheet's client filter. */
export interface ClientOption { id: string; name: string }

export function query(params: object): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    // Undefined and empty are omitted, so the server applies its own defaults rather
    // than receiving `sort=undefined`.
    if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
  }
  const s = q.toString();
  return s ? `?${s}` : '';
}

export const hierarchy = {
  agencies: (p: ListParams) => api<Page<Agency>>(`/v1/hierarchy/agencies${query(p)}`),
  agency: (id: string) => api<Agency>(`/v1/hierarchy/agencies/${id}`),
  agencyClients: (id: string, p: ListParams) =>
    api<Page<Client>>(`/v1/hierarchy/agencies/${id}/clients${query(p)}`),

  clients: (p: ListParams & { agencyId?: string }) => api<Page<Client>>(`/v1/hierarchy/clients${query(p)}`),
  client: (id: string) => api<Client>(`/v1/hierarchy/clients/${id}`),
  clientCampaigns: (id: string, p: ListParams) =>
    api<Page<Campaign>>(`/v1/hierarchy/clients/${id}/campaigns${query(p)}`),

  campaigns: (p: ListParams & { agencyId?: string; clientId?: string; excludeEblast?: boolean }) =>
    api<Page<Campaign>>(`/v1/hierarchy/campaigns${query({ ...p, clientIds: p.clientIds?.length ? p.clientIds.join(',') : undefined })}`),

  /** Distinct clients that have campaigns matching a window/search — the spreadsheet's client-filter options. */
  campaignClients: (f: { ranFrom?: string; ranTo?: string; search?: string }) =>
    api<ClientOption[]>(`/v1/hierarchy/campaigns/clients${query({ ranFrom: f.ranFrom, ranTo: f.ranTo, search: f.search })}`),
  campaign: (id: string) => api<Campaign>(`/v1/hierarchy/campaigns/${id}`),
  campaignAdGroups: (id: string, p: ListParams) =>
    api<Page<AdGroup>>(`/v1/hierarchy/campaigns/${id}/adgroups${query(p)}`),

  adGroup: (id: string) => api<AdGroup>(`/v1/hierarchy/adgroups/${id}`),
  adGroupAds: (id: string, p: ListParams) =>
    api<Page<Ad>>(`/v1/hierarchy/adgroups/${id}/ads${query(p)}`),

  /** One ad, NORMALISED (with `creative` resolved by the legacy rules) — for the edit form's preview. */
  ad: (id: string) => api<Ad>(`/v1/hierarchy/ads/${id}`),

  adGroupDelivery: (id: string) =>
    api<AdGroupDelivery>(`/v1/hierarchy/adgroups/${id}/delivery`),

  adGroupsDelivery: (ids: string[]) =>
    ids.length === 0
      ? Promise.resolve({} as Record<string, AdGroupRowDelivery>)
      : api<Record<string, AdGroupRowDelivery>>(
          `/v1/hierarchy/adgroups/delivery?ids=${encodeURIComponent(ids.join(','))}`),

  /**
   * What OUR store says a campaign delivered — the figures a report is built from.
   *
   * Distinct from `campaignPacing`, which reads the legacy database. Same aggregation the
   * generate worker calls, so this screen and the client's PDF cannot disagree.
   */
  campaignStoreDelivery: (id: string) =>
    api<CampaignStoreDelivery>(`/v1/hierarchy/campaigns/${encodeURIComponent(id)}/delivery-from-store`),

  campaignPacing: (id: string) => api<CampaignPacing>(`/v1/hierarchy/campaigns/${id}/pacing`),

  campaignDelivery: (ids: string[]) =>
    ids.length === 0
      ? Promise.resolve({} as Record<string, CampaignDelivery>)
      : api<Record<string, CampaignDelivery>>(
          `/v1/hierarchy/campaigns/delivery?ids=${encodeURIComponent(ids.join(','))}`),

  /** Per-campaign spreadsheet metrics (simulated + actual impressions, clicks, retargeting, contract). */
  campaignMetrics: (ids: string[], range?: { start?: string; end?: string }) =>
    ids.length === 0
      ? Promise.resolve({} as Record<string, CampaignMetrics>)
      : api<Record<string, CampaignMetrics>>(
          `/v1/hierarchy/campaigns/metrics${query({ ids: ids.join(','), start: range?.start, end: range?.end })}`),
};

export interface EmailCampaign {
  id: string; name: string; so: string; reportSo: string; subject: string; fromName: string;
  fromEmail: string; status: string; clientId: string; client: string;
  totalRecords: number; totalOpens: number; uniqueOpens: number; opensPct: number;
  totalClicks: number; clicksPct: number; totalBounces: number; bouncePct: number; optOut: number;
  io: string; geotype: string;
  startDatetime: string | null; endDatetime: string | null; createDatetime: string | null;
}

/** One drop inside an SO group (the accordion rows). */
export interface EmailDrop {
  id: string; name: string; status: string; deploy: string | null;
  records: number; opens: number; clicks: number; opensPct: number; clicksPct: number;
}

/** One SO with its drops folded in — the "Group by SO" row shape. */
export interface EmailSoGroup {
  so: string; reportSo: string; name: string; clientId: string; client: string;
  dropCount: number; deployDates: string[]; firstDeploy: string | null; lastDeploy: string | null;
  /** Whole-campaign status across all deploys — COMPLETED only when every deploy has finished. */
  status: string; latestEnd: string | null;
  totalRecords: number; totalOpens: number; totalClicks: number; opensPct: number; clicksPct: number;
  drops: EmailDrop[];
}

/** One tracked link's raw click performance. */
export interface EmailLinkStat { link: string; clicks: number; uniqueClicks: number; mobileClicks: number }

/** One deployment's detail-page metrics. */
export interface EmailDeployDetail extends EmailCampaign {
  networkStatus: string; mobileClicks: number; nonMobileClicks: number; openRate: number; ctr: number;
  creativeUrl: string | null; links: EmailLinkStat[]; linkClicksTotal: number;
}

type EmailListParams = Omit<ListParams, 'status'> & {
  status?: string; startDate?: string; endDate?: string; clientIds?: string[];
};
const emailQuery = (p: EmailListParams) => query({ ...p, clientIds: p.clientIds?.length ? p.clientIds.join(',') : undefined });

/** Email (eblast) campaigns — a separate collection from the RTB hierarchy. */
export const email = {
  campaigns: (p: EmailListParams) => api<Page<EmailCampaign>>(`/v1/email/campaigns${emailQuery(p)}`),
  grouped: (p: EmailListParams) => api<Page<EmailSoGroup>>(`/v1/email/campaigns/grouped${emailQuery(p)}`),
  clients: (p: EmailListParams) => api<ClientOption[]>(`/v1/email/campaigns/clients${emailQuery(p)}`),
  deploy: (id: string) => api<EmailDeployDetail>(`/v1/email/campaigns/${encodeURIComponent(id)}`),
};

export interface CampaignMetrics {
  contractedImpressions: number | null;
  impressionsSim: number;
  impressionsActual: number;
  clicks: number;
  retargetImpressions: number;
  retargetClicks: number;
}

// ---- The report builder ----------------------------------------------------

import type { ResolvedDocument } from '@pdmg/reporting-bindings';

export type OverrideKind = 'DATA_GAP' | 'CONTRACTUAL' | 'PRESENTATION';

export interface StoredOverrideRow {
  path: string; value: number | string | boolean; kind: OverrideKind; reason?: string;
  userId: string; userName?: string; sequence: number; createdAt: string;
  originalValue: number | string | boolean | null;
}

export interface SettingsField {
  path: string;
  label: string;
  kind: 'colour' | 'boolean' | 'text' | 'date' | 'integer';
  help?: string;
  value: number | string | boolean | null;
}

export interface BuilderView {
  reportId: string; reportVersion: number; reportSo: string; clientName: string;
  status: string;
  /** Legal next statuses from `status` (server is the authority; DELIVERED excluded). */
  nextStatuses: string[];
  lifecycle: string; metricsVersion: 'v1' | 'v2';
  /** The report's channels, e.g. ['DISPLAY'] / ['EMAIL']. Simulated impressions apply only to display. */
  channels?: string[];
  templateId: string; templateVersion: number; generatedAt: string;
  document: ResolvedDocument;
  editable: string[];
  settings: SettingsField[];
  changed: string[];
  /**
   * Reconciliation status for the last-changed figure of a summing group (device mobile+desktop, or
   * the browser breakdown): 'ok' = sums to total clicks (green), 'bad' = does not (red). Keyed by path.
   */
  reconcile: Record<string, 'ok' | 'bad'>;
  /** Set when the stated reporting period no longer matches the generated one. */
  periodDrift: {
    generated: { start: string; end: string };
    stated: { start: string; end: string };
  } | null;
  /** The report's window — drives the review screen's date-range control (rebuild-for-window). */
  periodStart: string;
  periodEnd: string;
  /** ISO instant when the window reaches today — the figures are provisional and re-settle. Null otherwise. */
  provisionalAsOf: string | null;
  /** A reviewer has marked the report final (campaign complete). Gates Approve; drops the provisional note. */
  final: boolean;
  overrides: StoredOverrideRow[];
  consequentialChanges: number;
  violations: string[];
}

/**
 * The CSRF token, fetched once and reused.
 *
 * The session itself is an httpOnly cookie this file cannot read, which is the point.
 * The token is not secret in the same way — it is useless without that cookie — so
 * caching it in memory is safe, and re-fetching it on every edit would put a round trip
 * in front of every keystroke-committed change.
 */
let csrfToken: string | null = null;

export interface SessionUser {
  id: string; email: string; name?: string;
  /** 'admin' unlocks account management; 'reviewer' does not. */
  role?: 'admin' | 'reviewer';
}

export async function session(): Promise<{ user: SessionUser | null; csrfToken: string | null }> {
  const s = await api<{ user: SessionUser | null; csrfToken: string | null }>('/v1/session');
  csrfToken = s.csrfToken;
  return s;
}

async function post<T>(path: string, body: Record<string, unknown>): Promise<T> {
  if (csrfToken === null) await session();
  const send = () => api<T>(path, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ ...body, _csrf: csrfToken }),
  });
  try {
    return await send();
  } catch (e) {
    /*
     * A stale token is recoverable exactly once.
     *
     * The session outlives the token when the server rotates it, and making the user
     * retype an edit because of that would be the wrong lesson to teach them. A second
     * failure is a real one and is allowed through.
     */
    if (e instanceof ApiError && e.status === 403) {
      await session();
      return send();
    }
    throw e;
  }
}

/** PUT, with the same one-shot CSRF recovery as post. The body IS the resource, so it is sent whole. */
async function put<T>(path: string, body: Record<string, unknown>): Promise<T> {
  if (csrfToken === null) await session();
  const send = () => api<T>(path, {
    method: 'PUT',
    headers: { 'content-type': 'application/json', 'x-csrf-token': csrfToken ?? '' },
    body: JSON.stringify(body),
  });
  try {
    return await send();
  } catch (e) {
    if (e instanceof ApiError && e.status === 403) { await session(); return send(); }
    throw e;
  }
}

/** DELETE, with the same one-shot CSRF recovery (token rides the x-csrf-token header — no body). */
async function del(path: string): Promise<void> {
  if (csrfToken === null) await session();
  const send = () => api<void>(path, { method: 'DELETE', headers: { 'x-csrf-token': csrfToken ?? '' } });
  try {
    await send();
  } catch (e) {
    if (e instanceof ApiError && e.status === 403) { await session(); await send(); return; }
    throw e;
  }
}

/**
 * Campaign / ad-group / ad MANAGEMENT — the native Surface B write API (`/v1/...`, session + CSRF).
 * Reads here go through Surface B too (the writable store), so screens read exactly what they wrote.
 * See docs/27-campaign-management-api.md.
 */
type Doc = Record<string, unknown>;
export const management = {
  campaigns: {
    get: (id: string) => api<{ campaign: Doc }>(`/v1/campaigns/${id}`),
    create: (body: Doc) => post<{ campaign: Doc }>('/v1/campaigns', body),
    update: (id: string, body: Doc) => put<{ message: string }>(`/v1/campaigns/${id}`, body),
    setStatus: (id: string, isActive: boolean) => put<{ message: string }>(`/v1/campaigns/${id}/status`, { is_active: isActive }),
    remove: (id: string) => del(`/v1/campaigns/${id}`),
    adGroups: (id: string) => api<{ ad_groups: Doc[] }>(`/v1/campaigns/${id}/adgroups`),
  },
  adGroups: {
    get: (id: string) => api<{ ad_group: Doc }>(`/v1/adgroups/${id}`),
    create: (body: Doc) => post<{ ad_group: Doc }>('/v1/adgroups', body),
    update: (id: string, body: Doc) => put<{ message: string }>(`/v1/adgroups/${id}`, body),
    setStatus: (id: string, isActive: boolean) => put<{ message: string }>(`/v1/adgroups/${id}/status`, { is_active: isActive }),
    remove: (id: string) => del(`/v1/adgroups/${id}`),
    ads: (id: string) => api<{ ads: Doc[] }>(`/v1/adgroups/${id}/ads`),
  },
  ads: {
    get: (id: string) => api<{ ad: Doc }>(`/v1/ads/${id}`),
    meta: (id: string) => api<{ ad: Doc; adgroup: Doc | null; meta: Doc }>(`/v1/ads/${id}/meta`),
    create: (body: Doc) => post<{ ad: Doc }>('/v1/ads', body),
    createMulti: (body: Doc) => post<{ ads: Doc[] }>('/v1/ads/multi', body),
    update: (id: string, body: Doc) => put<{ message: string }>(`/v1/ads/${id}`, body),
    setStatus: (id: string, isActive: boolean) => put<{ message: string }>(`/v1/ads/${id}/status`, { is_active: isActive }),
    remove: (id: string) => del(`/v1/ads/${id}`),
  },
  bidAdjustments: {
    get: (adgroupId: string) => api<{ bid_adjustment: Doc | null }>(`/v1/bid-adjustments/${adgroupId}`),
    setup: (body: Doc) => post<{ bid_adjustment: Doc }>('/v1/bid-adjustments/setup', body),
  },
  clients: {
    /** The raw client doc (snake_case fields) for the edit form. */
    get: (id: string) => api<{ client: Doc }>(`/v1/clients/${id}`),
    update: (id: string, body: Doc) => put<{ message: string }>(`/v1/clients/${id}`, body),
    setStatus: (id: string, isActive: boolean) => put<{ message: string }>(`/v1/clients/${id}/status`, { is_active: isActive }),
  },
};

/** A public-client-API key as the management surface returns it — never the secret or its hash. */
export interface ApiKeyRecord {
  id: string; clientId: string; label?: string; keyPrefix: string;
  createdAt: string; createdBy?: string; revokedAt?: string | null; lastUsedAt?: string | null;
}

/**
 * Public-client-API keys, issued and revoked by staff (session + CSRF). The raw key is returned by
 * `issue` exactly ONCE — it is never retrievable again, so the UI must surface it immediately.
 * `rotate` mints a fresh key and revokes the old one, returning the new raw key.
 */
export const apiKeys = {
  list: (clientId: string) =>
    api<{ keys: ApiKeyRecord[] }>(`/v1/api-keys${query({ clientId })}`).then((r) => r.keys),
  issue: (clientId: string, label?: string) =>
    post<{ apiKey: string; key: ApiKeyRecord }>('/v1/api-keys', { clientId, label }),
  revoke: (id: string) => del(`/v1/api-keys/${id}`),
  async rotate(clientId: string, oldId: string, label?: string) {
    const issued = await this.issue(clientId, label);
    await this.revoke(oldId);
    return issued;
  },
};

/**
 * Whether generation would succeed for this SO, resolved before the button is offered.
 *
 * The generate worker refuses a window it has no consolidated delivery for, rather than
 * emitting a report of zeros. This is that same answer given up front, so the screen can
 * explain instead of queueing a job that fails into a spinner.
 */
export interface GenerationReadiness {
  ready: boolean;
  /** The flight the report would cover — the whole SO, not one campaign. */
  window: { start: string; end: string };
  /** The store's overall delivery span, for "we have data up to …" when the window is newer. */
  coverage: { start: string; end: string } | null;
  /** Present only when generation could not be planned at all (no SO campaigns, bad window). */
  blocked?: string;
  /** The report channel this SO resolves to (audio / dooh / email / display). */
  channel?: string | null;
  /** True when the SO delivered on more than one channel — the report will be composed. */
  composite?: boolean;
  /** Set for a channel with a template but no generate build path yet (dooh, email). */
  unsupportedChannel?: string;
}

export interface CampaignReportLookup {
  campaignId: string;
  reportSo: string | null;
  campaignName: string | null;
  /** Null when no report has been generated for this campaign yet. */
  reportId: string | null;
  /** Null when a report already exists, there is no SO, or the server cannot probe. */
  generation: GenerationReadiness | null;
}

export interface DeliveryDay {
  date: string; impressions: number; clicks: number; settled: boolean;
}

export interface CampaignStoreDelivery {
  campaignId: string;
  reportSo: string | null;
  from: string; to: string;
  impressions: number; clicks: number;
  retargetImpressions: number; retargetClicks: number;
  mobileClicks: number; desktopClicks: number;
  /** Null when `bid_adjustments` carries no contract — never 0, which would read as a real one. */
  targetImpressions: number | null;
  targetClicks: number | null;
  /** Consolidated rows behind these figures. Zero means the window has not been built. */
  adRowCount: number;
  byDay: DeliveryDay[];
  provisionalDays: number;
}

export const builder = {
  load: (reportId: string) => api<BuilderView>(`/v1/builder/${encodeURIComponent(reportId)}`),

  forCampaign: (campaignId: string) =>
    api<CampaignReportLookup>(`/v1/builder/for-campaign/${encodeURIComponent(campaignId)}`),

  /** Resolve a stale/404 report id to the current report for the same SO (or null). Never throws a 404. */
  resolveStale: (reportId: string) =>
    api<{ reportId: string | null; reportSo: string | null; exists: boolean }>(
      `/v1/builder/resolve/${encodeURIComponent(reportId)}`),

  edit: (reportId: string, input: { path: string; value: number | string | boolean; kind: OverrideKind; reason?: string }) =>
    post<BuilderView>(`/v1/builder/${encodeURIComponent(reportId)}/overrides`, input),

  revert: (reportId: string, path: string) =>
    post<BuilderView>(`/v1/builder/${encodeURIComponent(reportId)}/overrides/revert`, { path }),

  /** Remove (or restore) a CTV app/channel or geo-target row; its impressions redistribute across the rest. */
  removeItem: (reportId: string, dimension: 'apps' | 'geoTargets', key: string, remove = true) =>
    post<BuilderView>(`/v1/builder/${encodeURIComponent(reportId)}/remove`, { dimension, key, remove }),

  /** Clear & reset: revert all figure/attribution edits back to generated, keeping presentation settings. */
  clear: (reportId: string) =>
    post<BuilderView>(`/v1/builder/${encodeURIComponent(reportId)}/clear`, {}),

  /** Templates a report can be re-rendered with — built-in and editor-authored. */
  templates: () =>
    api<Array<{ id: string; name: string; version: number; channels: string[]; source: 'built-in' | 'custom'; status?: string; v2?: boolean }>>(`/v1/builder/templates/list`),

  /** Re-template a report (review-stage). Pass templateId null to return to the generated template. */
  setTemplate: (reportId: string, templateId: string | null, templateVersion: number | null) =>
    post<BuilderView>(`/v1/builder/${encodeURIComponent(reportId)}/template`, { templateId, templateVersion }),

  /** The staff download URL for the rendered PDF — a session-authed GET, used as a plain link. */
  pdfUrl: (reportId: string) => `/v1/builder/${encodeURIComponent(reportId)}/pdf`,

  /** Rebuild a review-stage report in place (regenerate + re-render). Async: reload to see the result. */
  rebuild: (reportId: string) =>
    post<{ accepted: true; reportId: string; duplicate: boolean; campaignIds: string[] }>(
      `/v1/builder/${encodeURIComponent(reportId)}/rebuild`, {}),

  /** Move the report to another status (start review, request changes). Returns the refreshed view. */
  transition: (reportId: string, to: string) =>
    post<BuilderView>(`/v1/builder/${encodeURIComponent(reportId)}/transition`, { to }),

  /** Approve the report (IN_REVIEW → APPROVED). The approver is the session user; delivery follows. */
  approve: (reportId: string) =>
    post<BuilderView>(`/v1/builder/${encodeURIComponent(reportId)}/approve`, {}),

  /** Mark the report final (campaign complete), or reopen it. Gates approval; drops the provisional note. */
  finalize: (reportId: string, final: boolean) =>
    post<BuilderView>(`/v1/builder/${encodeURIComponent(reportId)}/finalize`, { final }),

  /** Simulated vs actual impressions for the report, for the compare panel. Clicks are identical in both. */
  impressionComparison: (reportId: string) =>
    api<ImpressionComparison>(`/v1/builder/${encodeURIComponent(reportId)}/impression-comparison`),

  /** Reconcile the report to simulated or actual impressions (persists the mode + rebuilds). */
  reconcile: (reportId: string, mode: 'sim' | 'actual') =>
    post<{ accepted: true; reportId: string; mode: 'sim' | 'actual'; duplicate: boolean }>(
      `/v1/builder/${encodeURIComponent(reportId)}/reconcile`, { mode }),
};

export interface ImpressionComparison {
  reportId: string;
  /** The report's current impression mode. */
  mode: 'sim' | 'actual';
  window: { start: string; end: string };
  campaignIds: string[];
  /** Whether the simulated/actual reconcile applies. False for CTV — video has no sim path. */
  simApplies?: boolean;
  /** Set when sim does not apply: which channel's note the panel shows. */
  channel?: 'video' | 'dooh';
  /** DOOH: null pre-flight (no delivery yet); else whether the figure is audience-weighted. */
  weighted?: boolean | null;
  /** DOOH: impressions the DOOH ads won on mobile / web supply — excluded from the report. */
  nonScreenImpressions?: number;
  simulated: { impressions: number; days: DeliveryDay[] };
  actual: { impressions: number; days: DeliveryDay[] };
}

export interface GenerateAccepted {
  accepted: boolean;
  reportId: string;
  campaignIds: string[];
  periodStart: string;
  periodEnd: string;
  lifecycle: string;
  /** A second request for the same report and window is a duplicate, not a second report. */
  duplicate: boolean;
  requestedBy: string;
}

/**
 * Ask for a report (D25).
 *
 * The unit is an SO, not a campaign — a buy is several campaigns and naming a subset would
 * produce a report that quietly omits delivery. It ENQUEUES: the response means a job was
 * accepted, not that a report exists, so callers poll rather than expecting a document back.
 */
export interface TemplateRef {
  id: string; version: number; name: string; status: string;
  channels: string[]; updatedAt: string; updatedBy: string;
}

/**
 * Template authoring (R2, D21).
 *
 * Admin-only on the server. A published version is immutable — `save` on one returns 409, which
 * the editor surfaces as "publish a new version" rather than a raw error.
 */
export const templates = {
  list: () => api<{ templates: TemplateRef[] }>('/v1/templates').then((r) => r.templates),
  get: (id: string, version: number) =>
    api<Record<string, unknown>>(`/v1/templates/${encodeURIComponent(id)}/${version}`),
  save: (id: string, version: number, template: Record<string, unknown>) =>
    put<{ written: boolean; reason?: string }>(`/v1/templates/${encodeURIComponent(id)}/${version}`, template),
};

export const reports = {
  generate: (input: { reportSo: string; periodStart?: string; periodEnd?: string; lifecycle?: string }) =>
    post<GenerateAccepted>('/v1/admin/reports/generate', input),
};

// ---- Accounts (admin only) --------------------------------------------------

/** One account as the management surface returns it — never a password, a hash, or a token. */
export interface Account {
  userId: string; email: string; name: string; role: 'admin' | 'reviewer';
  disabled: boolean; locked: boolean; lastLoginAt: string | null;
}

/** A freshly issued password-setup link, surfaced once for the admin to pass on. */
export interface SetupLink {
  email: string; url: string; emailed: boolean; reissued: boolean;
}

export interface AccountsView {
  accounts: Account[];
  me: { userId: string; email: string; name?: string; role: 'admin' | 'reviewer' };
  /** The environment credential that also signs in — it has no row and cannot be managed here. */
  breakGlassEmail: string | null;
}

/**
 * Account management — the admin-only JSON surface behind the Accounts screen (session + CSRF).
 *
 * There is no self-service signup (D10): `create` mints the account and returns a one-time setup
 * link shown to the admin to pass on; the new person sets their own password through the reset
 * flow. `resetLink` reissues that link (and clears any lockout). Emails are never editable and
 * accounts are disabled, never deleted — the audit trail references their ids for years (R2).
 */
export const accounts = {
  list: () =>
    api<{ users: Account[]; me: AccountsView['me']; breakGlassEmail: string | null }>('/v1/admin/users')
      .then((r) => ({ accounts: r.users, me: r.me, breakGlassEmail: r.breakGlassEmail } as AccountsView)),
  create: (input: { name: string; email: string; role: 'admin' | 'reviewer' }) =>
    post<{ ok: true; setupLink: SetupLink }>('/v1/admin/users', input),
  update: (userId: string, input: { name: string; role: 'admin' | 'reviewer' }) =>
    post<{ ok: true }>('/v1/admin/users/update', { userId, ...input }),
  resetLink: (userId: string) =>
    post<{ ok: true; setupLink: SetupLink }>('/v1/admin/users/reset-link', { userId }),
  setDisabled: (userId: string, disabled: boolean) =>
    post<{ ok: true }>('/v1/admin/users/disable', { userId, enable: disabled ? 'no' : 'yes' }),
};
